import { useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent } from 'react'

import type { MediaItem } from '@/components/media/MediaLightbox'
import { MediaLightbox, useMediaObjectUrl } from '@/components/media/MediaLightbox'
import {
  deleteProductMedia,
  getProductMedia,
  makePrimaryProductMedia,
  reorderProductMedia,
  uploadProductMedia,
} from '@/lib/api/client/inventory'
import type { ProductMedia } from '@/lib/api/schemas/inventory.schema'
import {
  BROWSER_IMAGE_POLICY,
  isSupportedBrowserImageMimeType,
  prepareBrowserImage,
} from '@/lib/media/browser-image-compression'
import type { BrowserImagePreparationReason } from '@/lib/media/browser-image-compression'
import { productMediaKindLabels } from '@/lib/warehouse/labels'

const ACCEPTED_IMAGE_UPLOAD_TYPES: ReadonlySet<string> = new Set<string>(BROWSER_IMAGE_POLICY.inputMimeTypes)
const ACCEPTED_UPLOAD_TYPES = new Set([...ACCEPTED_IMAGE_UPLOAD_TYPES, 'video/mp4'])
const ACCEPTED_UPLOADS = 'image/jpeg,image/png,image/webp,video/mp4'
const MAX_IMAGE_UPLOAD_BYTES = BROWSER_IMAGE_POLICY.maxBytes
const MAX_VIDEO_UPLOAD_BYTES = 100_663_296

type Notice = { readonly tone: 'info' | 'warn'; readonly text: string; readonly reason?: BrowserImagePreparationReason }
type UploadPhase = 'preparing' | 'uploading'
type UploadingFile = { readonly id: number; readonly name: string; readonly kind: 'image' | 'video'; readonly phase: UploadPhase }
type MediaPopulation = Pick<ProductMedia, 'imageTotal' | 'videoTotal' | 'otherFilesTotal' | 'unavailableReason' | 'unavailableMessage'>
type DropState = 'idle' | 'ready' | 'invalid'

function populationOf(media: ProductMedia): MediaPopulation {
  return {
    imageTotal: media.imageTotal,
    videoTotal: media.videoTotal,
    otherFilesTotal: media.otherFilesTotal,
    unavailableReason: media.unavailableReason,
    unavailableMessage: media.unavailableMessage,
  }
}

function aspect(item: MediaItem): string | null {
  if (item.widthPx === null || item.heightPx === null || item.heightPx === 0) return null
  const divisor = (left: number, right: number): number => right === 0 ? left : divisor(right, left % right)
  const common = divisor(item.widthPx, item.heightPx)
  return `${item.widthPx / common}:${item.heightPx / common}`
}

function duration(item: MediaItem): string | null {
  if (item.durationSeconds === null) return null
  const seconds = Math.round(item.durationSeconds)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

function apiUploadLabel(item: MediaItem): string | null {
  const actorPrefix = 'api_key:'
  if (item.source !== 'upload' || item.uploadedBy?.startsWith(actorPrefix) !== true) {
    return null
  }
  const name = item.uploadedBy.slice(actorPrefix.length).trim()
  return name.length > 0 ? `Загружено по API · ${name}` : null
}

function uploadContentType(fileOrType: File | string): string {
  const type = typeof fileOrType === 'string' ? fileOrType : fileOrType.type
  return type.split(';', 1)[0]?.trim().toLowerCase() ?? ''
}

function releasedUploadFile(file: File): File {
  const declared = uploadContentType(file)
  if ((declared === '' || declared === 'application/octet-stream') && /\.mp4$/i.test(file.name)) {
    return new File([file], file.name, { type: 'video/mp4' })
  }
  return file
}

function uploadByteLimit(contentType: string): number | null {
  if (ACCEPTED_IMAGE_UPLOAD_TYPES.has(contentType)) return MAX_IMAGE_UPLOAD_BYTES
  if (contentType === 'video/mp4') return MAX_VIDEO_UPLOAD_BYTES
  return null
}

function fileItemsOf(transfer: DataTransfer): DataTransferItem[] {
  return transfer.items === undefined ? [] : Array.from(transfer.items).filter((item) => item.kind === 'file')
}

function isFileTransfer(transfer: DataTransfer): boolean {
  return fileItemsOf(transfer).length > 0 || Array.from(transfer.types ?? []).includes('Files')
}

function dropStateOf(transfer: DataTransfer): Exclude<DropState, 'idle'> | null {
  const items = fileItemsOf(transfer)
  if (items.length === 0) return Array.from(transfer.types ?? []).includes('Files') ? 'ready' : null
  return items.every((item) => uploadByteLimit(uploadContentType(item.type)) !== null) ? 'ready' : 'invalid'
}

function UploadingTile({ file }: { file: UploadingFile }) {
  const status = file.kind === 'image'
    ? file.phase === 'preparing' ? 'Фото подготавливается…' : 'Подготовленная копия загружается…'
    : 'Видео загружается…'
  return (
    <li className="grid aspect-square place-items-center rounded-md border border-rule bg-bone/40 px-2 text-center text-[10px] text-ink-muted">
      <span role="status" aria-live="polite"><span>{file.name}</span><br /><span>{status}</span></span>
    </li>
  )
}

function MediaPreview({ item }: { item: MediaItem }) {
  const { objectUrl, failure } = useMediaObjectUrl(item.mediaUrl)
  const unavailable = item.status === 'unknown' ? 'Состояние файла неизвестно' : item.unavailableMessage
  if (failure) return <span role="alert" className="grid size-full place-items-center px-2 text-center text-[10px] text-st-unpaid">{failure}</span>
  if (item.mediaUrl === null) {
    return <span className="grid size-full place-items-center px-2 text-center text-[10px] text-ink-muted" data-reason={item.unavailableReason ?? undefined}>{unavailable}</span>
  }
  if (objectUrl === null) return <span aria-live="polite" className="grid size-full place-items-center text-[10px] text-ink-muted">Загружаем файл…</span>
  return item.kind === 'video'
    ? <video muted preload="metadata" src={objectUrl} className="size-full object-cover" />
    : <img src={objectUrl} alt="" className="size-full object-cover" />
}

function MediaTile({ item, ordinal, onOpen, onMakePrimary, onDelete, onDragStart, onDrop, className }: {
  item: MediaItem
  ordinal: number
  onOpen: () => void
  onMakePrimary: () => void
  onDelete: () => void
  onDragStart: () => void
  onDrop: () => void
  className?: string
}) {
  const measuredAspect = aspect(item)
  const measuredDuration = duration(item)
  const apiLabel = apiUploadLabel(item)
  return (
    <li
      className={`group relative aspect-square overflow-hidden rounded-md border border-rule bg-[#F7F3EA] ${className ?? ''}`}
      draggable
      onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; onDragStart() }}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move' }}
      onDrop={(event) => { event.preventDefault(); onDrop() }}
      data-media-id={item.id}
      data-media-kind={item.kind}
      title={item.probeUnavailableMessage ?? undefined}
    >
      <button type="button" onClick={onOpen} className="size-full cursor-zoom-in">
        <MediaPreview item={item} />
        <span className="absolute left-1.5 top-1.5 rounded bg-onyx/55 px-1.5 py-0.5 text-[10px] tabular-nums text-white">{ordinal}</span>
        {item.isPrimary ? <span className="absolute right-1.5 top-1.5 rounded-full bg-gold px-1.5 py-0.5 text-[9px] font-semibold text-onyx">Главное</span> : null}
        {apiLabel ? <span className="absolute bottom-1.5 left-1.5 max-w-[calc(100%-0.75rem)] truncate rounded bg-onyx/75 px-1.5 py-0.5 text-[9px] font-semibold text-white">{apiLabel}</span> : null}
        {item.kind === 'video' ? <span aria-hidden="true" className="absolute inset-0 grid place-items-center text-3xl text-white/90">▶</span> : null}
        {measuredAspect && !apiLabel ? <span className="absolute bottom-1.5 left-1.5 rounded bg-onyx/55 px-1 text-[9px] text-white">{measuredAspect}</span> : null}
        {measuredDuration ? <span className="absolute bottom-1.5 right-1.5 rounded bg-onyx/55 px-1 text-[9px] text-white">{measuredDuration}</span> : null}
      </button>
      <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1 bg-gradient-to-t from-onyx/60 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        {item.kind === 'image' && item.status === 'downloaded' && !item.isPrimary ? <button type="button" onClick={onMakePrimary} aria-label={`Сделать главным: ${ordinal}`} className="rounded bg-paper/90 px-1.5 py-0.5 text-xs text-gold-deep">★</button> : null}
        <button type="button" onClick={onDelete} aria-label={`Удалить: ${ordinal}`} className="rounded bg-paper/90 px-1.5 py-0.5 text-[10px] text-st-unpaid">Удалить</button>
      </div>
    </li>
  )
}

function MediaOverflowTile({ items, count, onOpen }: { items: readonly MediaItem[]; count: number; onOpen: () => void }) {
  return (
    <li className="relative aspect-square overflow-hidden rounded-md border border-rule bg-onyx-2">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Показать все медиафайлы (ещё ${count})`}
        className="relative grid size-full grid-cols-2 gap-px bg-onyx-3 p-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
      >
        {items.slice(0, 4).map((item) => (
          <span key={item.id} data-overflow-media-id={item.id} className="min-h-0 overflow-hidden bg-[#F7F3EA]">
            <MediaPreview item={item} />
          </span>
        ))}
        <span className="absolute inset-0 grid place-items-center bg-onyx/55">
          <span className="rounded-full bg-paper/95 px-2 py-1 text-xs font-semibold tabular-nums text-ink">+{count}</span>
        </span>
      </button>
    </li>
  )
}

function MediaDropOverlay({ state }: { state: DropState }) {
  if (state === 'idle') return null
  const valid = state === 'ready'
  return (
    <div
      data-testid="media-drop-overlay"
      className={`pointer-events-none absolute inset-0 z-20 grid place-items-center rounded-md border-2 bg-paper/90 p-4 text-center shadow-lg ${valid ? 'border-gold text-gold-deep' : 'border-st-unpaid text-st-unpaid'}`}
      aria-live="polite"
    >
      <span className="text-sm font-semibold">{valid ? 'Отпустите, чтобы загрузить' : 'Неверный файл'}</span>
    </div>
  )
}

function OtherFilesList({ items, total, onOpen, onDelete }: { items: readonly MediaItem[]; total: number; onOpen: (item: MediaItem) => void; onDelete: (item: MediaItem) => void }) {
  if (items.length === 0) return null
  return (
    <section aria-label={`Другие файлы · ${total}`} className="rounded-md border border-rule bg-bone/30 p-3">
      <h4 className="text-xs font-semibold text-ink">Другие файлы · {total}</h4>
      <ul className="mt-2 divide-y divide-rule text-xs">
        {items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-3 py-2" data-media-kind={item.kind}>
            <span className="min-w-0">
              <span className="block font-medium text-ink-2">▤ {productMediaKindLabels[item.kind]}</span>
              <span className="block truncate text-[10px] text-ink-muted">{item.status === 'unknown' ? 'Состояние файла неизвестно' : item.unavailableMessage ?? 'Файл готов'}</span>
            </span>
            <span className="flex shrink-0 gap-2">
              {item.mediaUrl !== null ? <button type="button" onClick={() => onOpen(item)} className="underline">Открыть</button> : null}
              <button type="button" onClick={() => onDelete(item)} className="text-st-unpaid">Удалить</button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** One shared mixed-media manager for the card and full product page. */
export function ProductMediaGallery({ media, columns = 4, heading, onRefresh, prepareImage = prepareBrowserImage }: { media: ProductMedia; columns?: 4 | 6; heading?: string; onRefresh?: (media: ProductMedia) => void; prepareImage?: typeof prepareBrowserImage }) {
  const [items, setItems] = useState<MediaItem[]>(media.items)
  const [population, setPopulation] = useState<MediaPopulation>(() => populationOf(media))
  const [openId, setOpenId] = useState<string | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [pendingDelete, setPendingDelete] = useState<{ item: MediaItem; label: string } | null>(null)
  const [uploading, setUploading] = useState<UploadingFile[]>([])
  const [showAll, setShowAll] = useState(false)
  const [dropState, setDropState] = useState<DropState>('idle')
  const dragDepth = useRef(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const nextUploadId = useRef(0)

  useEffect(() => {
    setItems(media.items)
    setPopulation(populationOf(media))
    setShowAll(false)
  }, [media])

  useEffect(() => {
    if (uploading.length === 0) return
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = 'Загрузка ещё не завершена.'
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [uploading.length])

  const merchandising = useMemo(() => items.filter((item) => item.kind === 'image' || item.kind === 'video'), [items])
  const otherFiles = useMemo(() => items.filter((item) => item.kind === 'instruction' || item.kind === 'unknown'), [items])
  const openIndex = openId === null ? -1 : items.findIndex((item) => item.id === openId)
  const compactCapacity = columns === 6 ? 9 : 5
  const hasOverflow = !showAll && merchandising.length > compactCapacity
  const visibleMerchandising = showAll || !hasOverflow
    ? merchandising
    : merchandising.slice(0, compactCapacity - 1)
  const overflowMerchandising = hasOverflow ? merchandising.slice(compactCapacity - 1) : []

  const refresh = async () => {
    const next = await getProductMedia(media.productId)
    setItems(next.items)
    setPopulation(populationOf(next))
    onRefresh?.(next)
  }

  const remove = async (item: MediaItem) => {
    try {
      await deleteProductMedia(media.productId, item.id)
      await refresh()
    } catch (reason: unknown) {
      setNotice({ tone: 'warn', text: reason instanceof Error ? reason.message : 'Изменения не сохранены.' })
    }
  }

  const ordinalOf = (item: MediaItem) => merchandising.findIndex((entry) => entry.id === item.id) + 1

  const requestRemove = (item: MediaItem) => {
    const ordinal = ordinalOf(item)
    const label = ordinal > 0 ? `${item.kind === 'video' ? 'видео' : 'фото'} ${ordinal}` : `файл «${productMediaKindLabels[item.kind]}»`
    setOpenId(null)
    setPendingDelete({ item, label })
  }

  const makePrimary = async (item: MediaItem) => {
    const ordinal = ordinalOf(item)
    try {
      await makePrimaryProductMedia(media.productId, item.id)
      await refresh()
      setNotice({ tone: 'info', text: `Главным стало фото ${ordinal} — оно переехало на первое место.` })
    } catch (reason: unknown) {
      setNotice({ tone: 'warn', text: reason instanceof Error ? reason.message : 'Изменения не сохранены.' })
    }
  }

  const reorder = async (targetId: string) => {
    if (draggedId === null || draggedId === targetId) return
    const previous = items
    const nextMerchandising = [...merchandising]
    const from = nextMerchandising.findIndex((item) => item.id === draggedId)
    let to = nextMerchandising.findIndex((item) => item.id === targetId)
    if (from < 0 || to < 0) return
    const [moved] = nextMerchandising.splice(from, 1)
    if (moved === undefined) return
    if (moved.kind === 'video' && to === 0 && nextMerchandising.some((item) => item.kind === 'image')) {
      to = 1
      setNotice({ tone: 'info', text: 'На первом месте всегда главное фото — видео туда не встаёт, оно останавливается на второй позиции.' })
    }
    nextMerchandising.splice(to, 0, moved)
    if (nextMerchandising[0]?.kind === 'video') {
      const firstPhoto = nextMerchandising.findIndex((item) => item.kind === 'image')
      if (firstPhoto > 0) {
        const [photo] = nextMerchandising.splice(firstPhoto, 1)
        if (photo !== undefined) nextMerchandising.unshift(photo)
        setNotice({ tone: 'info', text: 'На первом месте всегда главное фото — порядок скорректирован перед сохранением.' })
      }
    }
    const complete = [...nextMerchandising, ...otherFiles]
    setItems(complete.map((item, index) => ({ ...item, position: index + 1 })))
    try {
      await reorderProductMedia(media.productId, complete.map((item) => item.id))
      await refresh()
    } catch {
      setItems(previous)
      setNotice({ tone: 'warn', text: 'Не удалось сохранить порядок — вернули как было.' })
    } finally {
      setDraggedId(null)
    }
  }

  const upload = async (files: readonly File[]) => {
    for (const file of files) {
      const releasedFile = releasedUploadFile(file)
      const contentType = uploadContentType(releasedFile)
      const byteLimit = uploadByteLimit(contentType)
      if (!ACCEPTED_UPLOAD_TYPES.has(contentType) || byteLimit === null) {
        setNotice({ tone: 'warn', text: `${file.name} — формат не поддерживается. Нужны JPG, PNG, WEBP или MP4 (H.264/AVC; без аудио либо AAC-LC).`, reason: 'unsupported_type' })
        continue
      }
      if (releasedFile.size > byteLimit) {
        const limitMessage = contentType === 'video/mp4'
          ? 'файл больше 96 МиБ — загрузите видео поменьше.'
          : 'файл больше 20 МБ — загрузите фото поменьше.'
        setNotice({ tone: 'warn', text: `${file.name} — ${limitMessage}` })
        continue
      }
      const uploadId = nextUploadId.current + 1
      nextUploadId.current = uploadId
      const kind = contentType === 'video/mp4' ? 'video' : 'image'
      setUploading((current) => [...current, { id: uploadId, name: file.name, kind, phase: kind === 'image' ? 'preparing' : 'uploading' }])
      try {
        let uploadFile = releasedFile
        if (kind === 'image') {
          if (!isSupportedBrowserImageMimeType(contentType)) {
            setNotice({ tone: 'warn', text: `${file.name} — формат не поддерживается. Нужны JPG, PNG или WEBP.`, reason: 'unsupported_type' })
            continue
          }
          const preparation = await prepareImage(file)
          if (!preparation.ok) {
            setNotice({ tone: 'warn', text: `${file.name} — ${preparation.message}`, reason: preparation.reason })
            continue
          }
          uploadFile = preparation.file
          setUploading((current) => current.map((entry) => entry.id === uploadId ? { ...entry, phase: 'uploading' } : entry))
        }
        const written = await uploadProductMedia(media.productId, uploadFile)
        const duplicate = items.some((item) => item.id === written.id)
        await refresh()
        if (duplicate) {
          const label = written.kind === 'video' ? 'видео' : 'фото'
          setNotice({ tone: 'info', text: `Это ${label} уже загружено — оно уже есть в наборе.` })
        }
      } catch (reason: unknown) {
        setNotice({ tone: 'warn', text: `${file.name} — ${reason instanceof Error ? reason.message : 'Изменения не сохранены.'}` })
      } finally {
        setUploading((current) => current.filter((entry) => entry.id !== uploadId))
      }
    }
  }

  const handleDragEnter = (event: ReactDragEvent<HTMLDivElement>) => {
    const state = dropStateOf(event.dataTransfer)
    if (state === null) return
    event.preventDefault()
    dragDepth.current += 1
    setDropState(state)
  }

  const handleDragOver = (event: ReactDragEvent<HTMLDivElement>) => {
    const state = dropStateOf(event.dataTransfer)
    if (state === null) return
    event.preventDefault()
    event.dataTransfer.dropEffect = state === 'ready' ? 'copy' : 'none'
    setDropState(state)
  }

  const handleDragLeave = (event: ReactDragEvent<HTMLDivElement>) => {
    if (!isFileTransfer(event.dataTransfer)) return
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDropState('idle')
  }

  const handleDrop = (event: ReactDragEvent<HTMLDivElement>) => {
    if (!isFileTransfer(event.dataTransfer)) return
    event.preventDefault()
    dragDepth.current = 0
    setDropState('idle')
    void upload(Array.from(event.dataTransfer.files))
  }

  if (items.length === 0 && uploading.length === 0) {
    return (
      <div data-testid="product-media-gallery" className="relative" aria-busy={uploading.length > 0} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        <p className="text-xs text-ink-muted" data-reason={population.unavailableReason ?? undefined}>{population.unavailableMessage}</p>
        <p className="mt-1 text-[11px] text-ink-muted">Загрузите первое фото — оно станет главным автоматически; видео главным не становится.</p>
        {notice ? <p role={notice.tone === 'warn' ? 'alert' : 'status'} data-reason={notice.reason} className={notice.tone === 'warn' ? 'rounded-md bg-st-unpaid/5 px-3 py-2 text-xs text-st-unpaid' : 'rounded-md bg-gold/5 px-3 py-2 text-xs text-gold-deep'}>{notice.text}</p> : null}
        <button type="button" onClick={() => inputRef.current?.click()} className="mt-3 rounded-md border border-rule px-3 py-2 text-xs">Загрузить фото или видео</button>
        <input ref={inputRef} className="sr-only" type="file" accept={ACCEPTED_UPLOADS} multiple onChange={(event) => { void upload(Array.from(event.target.files ?? [])); event.currentTarget.value = '' }} />
        <MediaDropOverlay state={dropState} />
      </div>
    )
  }

  return (
    <div data-testid="product-media-gallery" className="relative space-y-3" aria-busy={uploading.length > 0} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-muted">
        <span>{population.imageTotal} фото · {population.videoTotal} видео · фото по порядку · WB: отдельная проекция видео</span>
        <button type="button" onClick={() => inputRef.current?.click()} className="rounded-md border border-rule px-2.5 py-1.5 text-ink-2">Загрузить</button>
        <input ref={inputRef} className="sr-only" type="file" accept={ACCEPTED_UPLOADS} multiple onChange={(event) => { void upload(Array.from(event.target.files ?? [])); event.currentTarget.value = '' }} />
      </div>
      {notice ? <p role={notice.tone === 'warn' ? 'alert' : 'status'} data-reason={notice.reason} className={notice.tone === 'warn' ? 'rounded-md bg-st-unpaid/5 px-3 py-2 text-xs text-st-unpaid' : 'rounded-md bg-gold/5 px-3 py-2 text-xs text-gold-deep'}>{notice.text}</p> : null}
      {pendingDelete ? (
        <div role="alertdialog" aria-label={`Удалить ${pendingDelete.label}?`} className="flex flex-wrap items-center gap-2 rounded-md border border-st-unpaid/30 bg-st-unpaid/5 px-3 py-2 text-xs text-ink">
          <span className="mr-auto">Удалить {pendingDelete.label}?</span>
          <button type="button" autoFocus onClick={() => setPendingDelete(null)} className="rounded-md border border-rule bg-paper px-2.5 py-1">Отмена</button>
          <button type="button" onClick={() => { const { item } = pendingDelete; setPendingDelete(null); void remove(item) }} className="rounded-md border border-st-unpaid/40 bg-paper px-2.5 py-1 text-st-unpaid">Удалить</button>
        </div>
      ) : null}
      <ul data-gallery-layout="featured" className={`grid gap-2 ${columns === 6 ? 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-6' : 'grid-cols-2 sm:grid-cols-4'}`} aria-label="Медиа: фото по порядку, видео отдельной проекцией WB">
        {visibleMerchandising.map((item, index) => <MediaTile key={item.id} item={item} ordinal={index + 1} onOpen={() => setOpenId(item.id)} onMakePrimary={() => { void makePrimary(item) }} onDelete={() => requestRemove(item)} onDragStart={() => setDraggedId(item.id)} onDrop={() => { void reorder(item.id) }} className={index === 0 && merchandising.length > 1 ? 'col-span-2 row-span-2' : undefined} />)}
        {overflowMerchandising.length > 0 ? <MediaOverflowTile items={overflowMerchandising} count={overflowMerchandising.length} onOpen={() => setShowAll(true)} /> : null}
        {uploading.map((file) => <UploadingTile key={file.id} file={file} />)}
      </ul>
      <p className="text-[10px] text-ink-muted">
        <button type="button" onClick={() => inputRef.current?.click()} className="underline decoration-dotted underline-offset-2 hover:text-ink">Перетащите сюда, чтобы загрузить</button>
        {' · Перетащите плитку, чтобы изменить порядок фото · WB: не более одного видео · клик — открыть просмотр'}
      </p>
      <OtherFilesList items={otherFiles} total={population.otherFilesTotal} onOpen={(item) => setOpenId(item.id)} onDelete={requestRemove} />
      {openIndex >= 0 ? <MediaLightbox entries={items.map((item) => ({ origin: 'media' as const, item }))} initialIndex={openIndex} heading={heading} onClose={() => setOpenId(null)} onMakePrimary={(item) => { void makePrimary(item) }} onDelete={requestRemove} /> : null}
      <MediaDropOverlay state={dropState} />
    </div>
  )
}
